import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Collapse, Container } from "reactstrap";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import RichText from "../../components/RichText/RichText";
import { faqData } from "../../content/faq";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  padding: 120px 0 80px;
  background: #ffffff;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    font-size: 36px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 20px;
  line-height: 1.6;
  color: #4a4a4a;
  margin-bottom: 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const FAQSection = styled.section`
  padding: 60px 0 80px;
  background: #f9fafb;
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const FAQHeader = styled.div`
  padding: 24px 32px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;

  &:hover {
    background: #f9fafb;
  }

  @media (max-width: 767px) {
    padding: 20px 24px;
  }
`;

const FAQQuestion = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  flex: 1;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

/** @type {import('styled-components').StyledComponent<'span', any, { isOpen?: boolean }, never>} */
const FAQIcon = styled.span`
  font-size: 24px;
  color: #6b7280;
  transition: transform 0.3s ease;
  transform: ${(/** @type {{ isOpen?: boolean }} */ props) => (props.isOpen ? "rotate(180deg)" : "rotate(0)")};
`;

const FAQBody = styled.div`
  padding: 0 32px 24px;

  @media (max-width: 767px) {
    padding: 0 24px 20px;
  }
`;

const FAQAnswer = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #4a4a4a;

  p {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 12px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const NewFAQPage = () => {
  const [openItem, setOpenItem] = useState(/** @type {string | null} */ (null));
  const location = useLocation();
  const { hash } = location;

  useEffect(() => {
    if (hash) {
      const faqId = hash.substring(1);
      const element = document.getElementById(faqId);
      if (element) {
        const timeoutId = setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          setOpenItem(faqId);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [hash]);

  /** @param {string} id */
  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
    window.history.pushState(null, "", `/faq#${id}`);
  };

  return (
    <PageContainer>
      <NewNavbar />

      <HeroSection>
        <Container>
          <PageTitle>Frequently Asked Questions</PageTitle>
          <PageSubtitle>Everything you need to know about Listino.</PageSubtitle>
        </Container>
      </HeroSection>

      <FAQSection>
        <Container>
          <FAQContainer>
            {faqData.map((faq) => (
              <FAQItem key={faq.id} id={faq.id}>
                <FAQHeader onClick={() => toggleItem(faq.id)}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <FAQIcon isOpen={openItem === faq.id}>▼</FAQIcon>
                </FAQHeader>
                <Collapse isOpen={openItem === faq.id}>
                  <FAQBody>
                    <FAQAnswer>
                      <RichText blocks={faq.answer} />
                    </FAQAnswer>
                  </FAQBody>
                </Collapse>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <NewFooter />
    </PageContainer>
  );
};

export default NewFAQPage;
